<script setup lang="ts">
import { amountRegex } from "~/constants/regex.constants";
import { sanitizeGFALAmountInput } from "~/utils/input.utils";

const emit = defineEmits(["close", "listingComplete"]);
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});

const GFALExStore = useGFALExStore();
const { t } = useI18n();
const { trackEvent } = useGAnalytics();
const { marketplaceFeesPercent, minimumPrice, hideRecommendedPrice } =
  useRuntimeConfig().public;
const isLoading = ref(false);
const errorMessageAmount = ref("");
const listingPrice = defineModel("listingPrice", { type: String });
const displayFees = ref(true);
const minimumFee = ref("");
const isAboveMinimumFee = ref(false);

watch(listingPrice, () => {
  const sanitizedValue = sanitizeGFALAmountInput(listingPrice.value || "");
  if ((listingPrice.value || "") !== sanitizedValue) {
    listingPrice.value = sanitizedValue;
    return;
  }

  if (errorMessageAmount.value) {
    validateInput();
  }
});

const listingPriceAfterFee = computed(() => {
  const minimumPriceGFAL = Number(minimumPrice) / GFALExStore.GFALExRate;
  const marketplaceFee =
    Number(listingPrice.value) * (Number(marketplaceFeesPercent) / 100);
  if (marketplaceFee > minimumPriceGFAL) {
    isAboveMinimumFee.value = true;
    return roundNum(Number(listingPrice.value) - Number(marketplaceFee));
  } else {
    isAboveMinimumFee.value = false;
    minimumFee.value = roundNum(minimumPriceGFAL);
    const total = Math.max(
      0,
      Number(listingPrice.value) - Number(minimumPriceGFAL)
    );
    return roundNum(total);
  }
});

const validateInput = () => {
  errorMessageAmount.value = "";
  if (!listingPrice.value) {
    return false;
  } else {
    if (GFALExStore.convertToUSD(listingPrice.value || "0") <= minimumPrice) {
      errorMessageAmount.value =
        t("errorMessages.transferInvalidMinimumPrice") +
        " " +
        Number(minimumPrice).toFixed(2) +
        " USD";
      return false;
    } else if (Number(listingPrice.value) < 0.01) {
      errorMessageAmount.value = t("errorMessages.transferInvalidAmount");
      return false;
    } else if (!amountRegex.test(listingPrice.value.toString())) {
      errorMessageAmount.value = t("errorMessages.listingInvalidCharacters");
      return false;
    } else if (isOverLimit(listingPrice.value)) {
      errorMessageAmount.value = t("errorMessages.transactionOverLimit");
      return false;
    } else {
      errorMessageAmount.value = "";
      return true;
    }
  }
};

const submitListing = () => {
  listingPrice.value = sanitizeGFALAmountInput(listingPrice.value || "");
  trackEvent("list_collectible_button");
  if (!validateInput()) return;
  isLoading.value = true;
  setTimeout(() => {
    emit("listingComplete");
  }, 500);
};

onMounted(() => {
  if (
    props.collectible.recommendedPrice.GFAL &&
    hideRecommendedPrice.toString() !== "true"
  ) {
    listingPrice.value = sanitizeGFALAmountInput(
      String(props.collectible.recommendedPrice.GFAL)
    );
  }
});
</script>
<template>
  <form class="flex flex-col gap-y-6" @submit.prevent="submitListing">
    <div class="flex gap-4 pb-2">
      <img
        class="w-16 object-contain border border-secondary-500"
        :src="getPortraitItemImage(collectible.imageId)"
        @error="
          (e) => ((e.target as HTMLImageElement).src = getPortraitItemImage())
        " />
      <div class="flex flex-col justify-center gap-1">
        <i18n-t
          tag="p"
          class="text-neutral-300"
          keypath="main.modalList.listingDetail"
          scope="global">
          <template #item>
            <span class="font-bold text-neutral-200">{{
              $t(`${collectible.translationId}.title`) +
              " " +
              $t(`${collectible.translationId}.subtitle`) +
              " #" +
              collectible.number
            }}</span>
          </template>
        </i18n-t>
      </div>
    </div>
    <div>
      <p class="text-xs font-bold text-neutral-300">
        {{ $t("main.modalList.setPrice") }}
      </p>
      <BaseInputGFAL v-model:amount="listingPrice" />
    </div>
    <i18n-t
      v-show="displayFees"
      tag="p"
      class="text-neutral-300 text-xs w-fit"
      keypath="main.modalList.feeInfo"
      scope="global">
      <template #price>
        <span class="font-bold text-neutral-200"
          >{{ listingPriceAfterFee }} GFAL</span
        >
      </template>
      <template #fee>
        <span class="font-bold text-neutral-200">{{
          $t("main.modalOffer.fee", {
            fee: isAboveMinimumFee
              ? Number(marketplaceFeesPercent) + "%"
              : minimumFee + " GFAL",
          })
        }}</span>
      </template>
    </i18n-t>
    <p class="text-error-300 text-xs h-4 -my-4">
      {{ errorMessageAmount }}
    </p>
    <div class="w-full mt-auto mb-2 flex items-center gap-4">
      <BaseButtonSubmit
        :loading="isLoading"
        :text="$t('main.modalList.listInMarketplace')"
        :disabled="isLoading || errorMessageAmount !== '' || !listingPrice"
        data-test="button-confirm-listing"
        :contextual-styles="true" />
      <BaseButtonCancel
        :text="$t('main.modalList.cancel')"
        data-test="button-dismiss-confirm-listing"
        :contextual-styles="true"
        @click="$emit('close')" />
    </div>
  </form>
</template>
